import React, {useContext, useEffect} from "react";
import moment from "moment-timezone";
import classNames from "classnames";
import {
    ButtonGroup,
    ConfirmationRequiredWrapper,
    RoundedTable,
    SearchByCreatedSwitch,
    TableDateSearch
} from "../index.jsx";
import {TableDateSearchContext} from "../../admin/Context/TableDateSearchContext.jsx";
import {ConfigProvider, Dropdown, Menu, Tooltip} from "antd";
import {NOOP} from "../../utils/NOOP.jsx";
import "./orders-date-range-table.css";
import {useItemListViewActions} from "./useItemListViewActions.jsx";
import {EllipsisOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import {BriefShortcutFilterValues} from "../../company/CompanyAccountProfile/CompanyBriefs/consts.jsx";

export const TableDateRangeTable = ({
    title,
    subTitle,
    data = [],
    isLoadingOrderTable = false,
    columns = NOOP,
    shortcutSearchOptions = [],
    defaultShortcutSearchOptions,
    onInputSearch = NOOP,
    statisticsLabel,
    log = NOOP,
    expandable = {expandedRowRender: NOOP, rowExpandable: NOOP},
    pagination,
    getRowClass = NOOP,
    tableClassName,
    disableFutureDates = true,
    showSearchByCreatedAt = true,
    allowDeselectShortcutSearchOptions = true,
    showDateRangeFilter = true,
    actions = [],
    hideColumns = [],
    onChange = NOOP,
    maxDateRange = 1,
    maxDateRangeUnit = "month",
    enforceMaxDateRange = false,
    hideDatesPicker = false,
    hideRangePicker = false,
    onShortcutSearchChange = NOOP,
    extraContent = null,
    rowKey = "eventId"
}) => {
    const {onShortcutClick, onDatePicker, onSearchByCreatedAtChange, selectedOption} = useItemListViewActions(
        shortcutSearchOptions,
        log,
        defaultShortcutSearchOptions
    );

    useEffect(() => {
        const currentShortcutSearchOption = shortcutSearchOptions.find(option => option.value === selectedOption);
        if (currentShortcutSearchOption?.value === BriefShortcutFilterValues.AWAITING_ADMIN_APPROVAL) {
            onShortcutSearchChange(true);
        } else {
            onShortcutSearchChange(false);
        }
    }, [selectedOption, shortcutSearchOptions, onShortcutSearchChange]);

    const {searchDate, searchByCreated} = useContext(TableDateSearchContext);

    return (
        <div className="orders-date-table">
            <ConfigProvider direction="rtl">
                {title || subTitle ? (
                    <div className="title-container">
                        {!!title && <div className="title">{title}</div>}
                        {!!subTitle && <div className="sub-title">{subTitle}</div>}
                    </div>
                ) : null}

                {!!shortcutSearchOptions?.length ? (
                    <div className="shortcut-search-container">
                        <ButtonGroup
                            onSelect={onShortcutClick}
                            options={shortcutSearchOptions}
                            selectedOption={selectedOption}
                            defaultSelectedOption={defaultShortcutSearchOptions}
                            allowDeselect={allowDeselectShortcutSearchOptions}
                        />
                        {extraContent}
                    </div>
                ) : (
                    extraContent
                )}

                <div className="orders-date-search-row">
                    {!!showDateRangeFilter && (
                        <div className="date-search-container">
                            <TableDateSearch
                                date={searchDate}
                                onDatePicked={onDatePicker}
                                onSearch={onInputSearch}
                                disableFuture={disableFutureDates && searchByCreated}
                                allowClear={false}
                                defaultValue={[moment().startOf("month"), moment().endOf("month")]}
                                maxDateRange={maxDateRange}
                                maxDateRangeUnit={maxDateRangeUnit}
                                enforceMaxDateRange={enforceMaxDateRange}
                                hideDatesPicker={hideDatesPicker}
                                hideRangePicker={hideRangePicker}
                            />
                            {!!showSearchByCreatedAt && (
                                <SearchByCreatedSwitch
                                    searchByCreated={searchByCreated}
                                    onChange={onSearchByCreatedAtChange}
                                />
                            )}
                        </div>
                    )}

                    {!!statisticsLabel && <div className="statistic-label-container">{statisticsLabel}</div>}
                </div>

                <ConfigProvider direction="rtl">
                    <RoundedTable
                        rowKey={rowKey}
                        scroll={{x: 1400}}
                        className={tableClassName}
                        pagination={pagination}
                        rowClassName={getRowClass}
                        loading={isLoadingOrderTable}
                        dataSource={data || []}
                        columns={[
                            ...columns.filter(c => !hideColumns.includes(c.key)),
                            ...(actions?.length
                                ? [
                                      {
                                          width: 10,
                                          render: (_, item) => {
                                              return <TableDateRangeTableAction actions={actions} item={item} />;
                                          }
                                      }
                                  ]
                                : [])
                        ]}
                        expandable={expandable}
                        onChange={onChange}
                    />
                </ConfigProvider>
            </ConfigProvider>
        </div>
    );
};

export const TableDateRangeTableAction = ({actions, item}) => {
    const renderAction = (action, index) => {
        const isDisabled = typeof action.disabled === "function" ? action.disabled(item) : action.disabled;
        const title = typeof action.title === "function" ? action.title(item) : action.title;
        const danger = action.danger === true;
        const color = typeof action.color === "function" ? action.color(item, isDisabled) : action.color;
        const titleStyle = color && !isDisabled ? {color} : {};
        const tooltip = typeof action.tooltip === "function" ? action.tooltip(item) : action.tooltip;
        const icon = typeof action.icon === "function" ? action.icon(item) : action.icon;

        if (action.needConfirmation) {
            return (
                <Menu.Item key={index} className={action.className} disabled={isDisabled} danger={danger}>
                    <ConfirmationRequiredWrapper
                        disabled={isDisabled}
                        onYes={() => !isDisabled && action.onClick(item)}>
                        {icon && (
                            <span className={classNames("orders-date-table-action-icon", {disabled: isDisabled})}>
                                {icon}
                            </span>
                        )}
                        <span style={titleStyle}>{title}</span>
                        {!!tooltip && (
                            <span>
                                <Tooltip title={tooltip}>
                                    <QuestionCircleOutlined style={{cursor: "pointer"}} />
                                </Tooltip>
                            </span>
                        )}
                    </ConfirmationRequiredWrapper>
                </Menu.Item>
            );
        }

        return (
            <Menu.Item
                disabled={isDisabled}
                className={action.className}
                key={index}
                danger={danger}
                onClick={() => !isDisabled && action.onClick(item)}>
                {icon && (
                    <span className={classNames("orders-date-table-action-icon", {disabled: isDisabled})}>{icon}</span>
                )}
                <span style={titleStyle}>{title}</span>
                {!!tooltip && (
                    <span>
                        <Tooltip title={tooltip}>
                            <QuestionCircleOutlined style={{cursor: "pointer"}} />
                        </Tooltip>
                    </span>
                )}
            </Menu.Item>
        );
    };

    const renderMenuItems = () => {
        const menuItems = [];

        actions?.forEach((action, index) => {
            // Divider-only action: { divider: true }
            if (action.title === "divider") {
                menuItems.push(<Menu.Divider key={`divider-${index}`} />);
                return;
            }

            // Grouped action: { title: "Group Title", children: [...] }
            if (action.children && Array.isArray(action.children)) {
                // Filter children by shouldHide
                const visibleChildren = action.children.filter(childAction => {
                    if (childAction.shouldHide === true) {
                        return false;
                    }
                    if (typeof childAction.shouldHide === "function") {
                        return !childAction.shouldHide(item);
                    }
                    return true;
                });

                // Only render group if there are visible children
                if (visibleChildren.length > 0) {
                    const groupTitle = typeof action.title === "function" ? action.title(item) : action.title;
                    menuItems.push(
                        <Menu.ItemGroup
                            key={`group-${index}`}
                            title={<span style={{fontWeight: "bold", color: "#000"}}>{groupTitle}</span>}>
                            {visibleChildren.map((childAction, childIndex) =>
                                renderAction(childAction, `group-${index}-child-${childIndex}`)
                            )}
                        </Menu.ItemGroup>
                    );
                }
            } else {
                const shouldHide =
                    typeof action?.shouldHide === "function" ? action.shouldHide(item) : action?.shouldHide;

                if (!shouldHide) {
                    menuItems.push(renderAction(action, index));
                }
            }
        });

        return menuItems;
    };

    return (
        <Dropdown overlay={<Menu>{renderMenuItems()}</Menu>} trigger={["click"]}>
            <EllipsisOutlined className="admin-orders-order-action-button" />
        </Dropdown>
    );
};
